import {
  Box,
  Button,
  SelectChangeEvent,
  Stack,
  Typography,
  FormControlLabel,
  Switch,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useState } from "react";
import useResponsive from "../../hooks/useResponsive";
import { useNavigate, useLocation } from "react-router-dom";

import { PostJobProps } from "../../interface/job_board";
import dayjs, { Dayjs } from "dayjs";
import JobApplication from "./JobApplication";
import PreviewJob from "./PreviewJob";
import TokenWarning from "../UIElements/token-warning";
import { PATH_PROFILE } from "../../routes/path";
import { useWalletSettings } from "../../hooks/useWalletSetting";
import { useAuth } from "../../hooks/useAuth";
import { useNotificationService } from "../notification";

const PostJobContent = () => {
  const theme = useTheme();
  const isMedium = useResponsive("down", "md", "xl");
  const [type, setType] = useState("full time");
  const navigate = useNavigate();
  const location = useLocation();
  const { token } = useAuth();

  const { conversionRates, userBalance } = useWalletSettings(token);

  const [preview, setPreview] = useState(true);
  // Read defaultOutsourced from router navigation state (set by "Hire for me" button)
  const [isOutsourced, setIsOutsourced] = useState<boolean>(
    location.state?.defaultOutsourced ?? false,
  );
  const [outsourcingDetails, setOutsourcingDetails] = useState({
    positions_needed: 1,
    urgency: "medium" as "low" | "medium" | "high" | "critical",
    deadline: "",
    special_requirements: "",
    budget_range: {
      min: undefined as number | undefined,
      max: undefined as number | undefined,
    },
  });

  const headerSty = {
    fontFamily: "Lexend",
    fontWeight: 500,
    my: isMedium ? 2 : 3,
    color: theme.palette.neutral.light,
  };

  const btnSty = {
    paddingY: 2,
    paddingX: 4,
    fontFamily: "DM Sans",
    fontWeight: 700,
    fontSize: "12px",
    borderRadius: "12px",
    "&: hover": {
      background: "inherit",
    },
  };

  const [job_details, setJob_Details] = useState<PostJobProps>({
    job_title: "",
    work_location: "",
    job_description: "",
    how_to_apply: "",
    application_deadline: "",
    experience_level: "",
    work_schedule: "",
    job_type: "",
    department: "",
    qualifications: "",
    employment_type: "",
    work_type: "on-site",
    additional_info: "",
    salary: "",
    external_link: "",
  });

  const [selectedDate, setSelectedDate] = useState<Dayjs | null>(null);
  const [documents, setDocuments] = useState<string[]>([]);
  const [benefits, setBenefits] = useState<string[]>([]);
  const [contract_duration, setContract_duration] = useState<any>({
    startDate: new Date().toISOString(),
    endDate: new Date().toISOString(),
  });

  const [typed_department, setTyped_Department] = useState("");
  const [nurses, setNurses] = useState<string[]>([]);
  const { showNotification } = useNotificationService();

  const handleNurses = (event: SelectChangeEvent<typeof benefits>) => {
    const {
      target: { value },
    } = event;
    setNurses(typeof value === "string" ? value.split(",") : value);
  };

  const handleDocument = (event: SelectChangeEvent<typeof documents>) => {
    const {
      target: { value },
    } = event;

    const newSelection = typeof value === "string" ? value.split(",") : value;
    const filteredSelection = Array.from(new Set([...newSelection]));

    setDocuments(filteredSelection);
  };

  const handleBenefits = (event: SelectChangeEvent<typeof benefits>) => {
    const {
      target: { value },
    } = event;

    const selectedValues = typeof value === "string" ? value.split(",") : value;

    if (selectedValues.includes("No benefits")) {
      setBenefits(["No benefits"]);
    } else {
      const filteredValues = selectedValues.filter(
        (val) => val !== "No benefits",
      );
      setBenefits(filteredValues);
    }
  };

  const handleDateChange = (newValue: Dayjs) => {
    if (!newValue) return;

    if (newValue.isBefore(dayjs(), "day")) {
      showNotification("Date older than today", "error");
      return;
    }
    setSelectedDate(newValue);
  };

  const handlePreview = () => {
    if (job_details.job_title === "Nurse" && !!nurses) {
      setJob_Details((prev) => ({
        ...prev,
        qualifications: nurses?.toString(),
      }));
    }
    if (typed_department?.length > 1) {
      setJob_Details((prev) => ({ ...prev, department: typed_department }));
      setPreview(!preview);
    } else {
      setPreview(!preview);
    }
  };

  return (
    <Stack sx={{ paddingX: 2 }}>
      <Box
        sx={{
          margin: 2,
          padding: isMedium ? 3 : 4,
          borderRadius: "3px",
          minHeight: "50vh",
          backgroundColor: theme.palette.common.white,
        }}
      >
        {preview && (
          <Box>
            <Stack
              direction={"row"}
              justifyContent={"space-between"}
              sx={{ mb: 3 }}
            >
              <Typography
                sx={{ ...headerSty, my: 0, mb: 0 }}
                variant="subtitle2"
              >
                Post a Job
              </Typography>

              <TokenWarning
                availableTokens={userBalance || 0}
                requiredTokens={conversionRates?.job || 0}
                onBuyTokens={() => navigate(PATH_PROFILE.root)}
              />

              <Button
                onClick={handlePreview}
                sx={{ borderRadius: "12px", width: 120 }}
                variant="contained"
              >
                Preview
              </Button>
            </Stack>
            {/* Outsourcing Toggle */}
            <Box
              sx={{
                p: 3,
                mb: 3,
                borderRadius: "12px",
                backgroundColor: "#F8F9FA",
                border: `2px solid ${isOutsourced ? theme.palette.primary.main : "#E0E0E0"}`,
              }}
            >
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
              >
                <Box>
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontFamily: "Lexend",
                      fontWeight: 600,
                      color: theme.palette.neutral.dark,
                      mb: 0.5,
                    }}
                  >
                    Let us hire for you
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      fontFamily: "DM Sans",
                      color: theme.palette.neutral.light,
                    }}
                  >
                    Our team will handle the recruitment process and find the
                    best candidates
                  </Typography>
                </Box>
                <FormControlLabel
                  control={
                    <Switch
                      checked={isOutsourced}
                      onChange={(e) => setIsOutsourced(e.target.checked)}
                      color="primary"
                    />
                  }
                  label=""
                />
              </Stack>

              {/* Outsourcing Details - Show when toggle is on */}
              {isOutsourced && (
                <Box sx={{ mt: 3, pt: 3, borderTop: "1px solid #E0E0E0" }}>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontFamily: "Lexend",
                      fontWeight: 500,
                      mb: 2,
                      color: theme.palette.neutral.dark,
                    }}
                  >
                    Outsourcing Details
                  </Typography>
                  <Stack spacing={2.5}>
                    {/* Positions Needed */}
                    <Box>
                      <Typography
                        sx={{
                          mb: 1,
                          fontSize: "14px",
                          fontWeight: 500,
                          color: "#404E57",
                        }}
                      >
                        Positions Needed *
                      </Typography>
                      <input
                        type="number"
                        min="1"
                        required
                        value={outsourcingDetails.positions_needed}
                        onChange={(e) =>
                          setOutsourcingDetails({
                            ...outsourcingDetails,
                            positions_needed: parseInt(e.target.value) || 1,
                          })
                        }
                        style={{
                          width: "100%",
                          padding: "12px",
                          borderRadius: "8px",
                          border: "1px solid #E0E0E0",
                          fontSize: "14px",
                          fontFamily: "DM Sans",
                          boxSizing: "border-box",
                        }}
                      />
                    </Box>

                    {/* Urgency Level */}
                    <Box>
                      <Typography
                        sx={{
                          mb: 1,
                          fontSize: "14px",
                          fontWeight: 500,
                          color: "#404E57",
                        }}
                      >
                        Urgency Level *
                      </Typography>
                      <select
                        value={outsourcingDetails.urgency}
                        onChange={(e) =>
                          setOutsourcingDetails({
                            ...outsourcingDetails,
                            urgency: e.target.value as
                              | "low"
                              | "medium"
                              | "high"
                              | "critical",
                          })
                        }
                        style={{
                          width: "100%",
                          padding: "12px",
                          borderRadius: "8px",
                          border: "1px solid #E0E0E0",
                          fontSize: "14px",
                          fontFamily: "DM Sans",
                          backgroundColor: "white",
                          boxSizing: "border-box",
                        }}
                      >
                        <option value="low">Low</option>
                        <option value="medium">Medium</option>
                        <option value="high">High</option>
                        <option value="critical">Critical</option>
                      </select>
                    </Box>

                    {/* Deadline */}
                    <Box>
                      <Typography
                        sx={{
                          mb: 1,
                          fontSize: "14px",
                          fontWeight: 500,
                          color: "#404E57",
                        }}
                      >
                        Deadline (Optional)
                      </Typography>
                      <input
                        type="date"
                        value={outsourcingDetails.deadline}
                        onChange={(e) =>
                          setOutsourcingDetails({
                            ...outsourcingDetails,
                            deadline: e.target.value,
                          })
                        }
                        style={{
                          width: "100%",
                          padding: "12px",
                          borderRadius: "8px",
                          border: "1px solid #E0E0E0",
                          fontSize: "14px",
                          fontFamily: "DM Sans",
                          boxSizing: "border-box",
                        }}
                      />
                    </Box>

                    {/* Budget Range */}
                    <Box>
                      <Typography
                        sx={{
                          mb: 1,
                          fontSize: "14px",
                          fontWeight: 500,
                          color: "#404E57",
                        }}
                      >
                        Budget Range (Optional)
                      </Typography>
                      <Stack
                        direction={isMedium ? "column" : "row"}
                        spacing={2}
                      >
                        <Box sx={{ width: isMedium ? "100%" : "50%" }}>
                          <Typography
                            sx={{
                              mb: 0.5,
                              fontSize: "12px",
                              color: "#6B7280",
                            }}
                          >
                            Minimum
                          </Typography>
                          <input
                            type="number"
                            min="0"
                            placeholder="Min budget"
                            value={outsourcingDetails.budget_range.min || ""}
                            onChange={(e) =>
                              setOutsourcingDetails({
                                ...outsourcingDetails,
                                budget_range: {
                                  ...outsourcingDetails.budget_range,
                                  min: e.target.value
                                    ? parseFloat(e.target.value)
                                    : undefined,
                                },
                              })
                            }
                            style={{
                              width: "100%",
                              padding: "12px",
                              borderRadius: "8px",
                              border: "1px solid #E0E0E0",
                              fontSize: "14px",
                              fontFamily: "DM Sans",
                              boxSizing: "border-box",
                            }}
                          />
                        </Box>
                        <Box sx={{ width: isMedium ? "100%" : "50%" }}>
                          <Typography
                            sx={{
                              mb: 0.5,
                              fontSize: "12px",
                              color: "#6B7280",
                            }}
                          >
                            Maximum
                          </Typography>
                          <input
                            type="number"
                            min="0"
                            placeholder="Max budget"
                            value={outsourcingDetails.budget_range.max || ""}
                            onChange={(e) =>
                              setOutsourcingDetails({
                                ...outsourcingDetails,
                                budget_range: {
                                  ...outsourcingDetails.budget_range,
                                  max: e.target.value
                                    ? parseFloat(e.target.value)
                                    : undefined,
                                },
                              })
                            }
                            style={{
                              width: "100%",
                              padding: "12px",
                              borderRadius: "8px",
                              border: "1px solid #E0E0E0",
                              fontSize: "14px",
                              fontFamily: "DM Sans",
                              boxSizing: "border-box",
                            }}
                          />
                        </Box>
                      </Stack>
                    </Box>

                    {/* Special Requirements */}
                    <Box>
                      <Typography
                        sx={{
                          mb: 1,
                          fontSize: "14px",
                          fontWeight: 500,
                          color: "#404E57",
                        }}
                      >
                        Special Requirements (Optional)
                      </Typography>
                      <textarea
                        rows={4}
                        placeholder="Any specific requirements or preferences..."
                        value={outsourcingDetails.special_requirements}
                        onChange={(e) =>
                          setOutsourcingDetails({
                            ...outsourcingDetails,
                            special_requirements: e.target.value,
                          })
                        }
                        style={{
                          width: "100%",
                          padding: "12px",
                          borderRadius: "8px",
                          border: "1px solid #E0E0E0",
                          fontSize: "14px",
                          fontFamily: "DM Sans",
                          resize: "vertical",
                          boxSizing: "border-box",
                        }}
                      />
                    </Box>
                  </Stack>
                </Box>
              )}
            </Box>
            <Typography variant="subtitle2" sx={{ ...headerSty }}>
              Job Type
            </Typography>
            <Box display={"flex"}>
              <Stack
                direction={"row"}
                sx={{
                  gap: 1,
                  background: "#284CB133",
                  p: 1,
                  borderRadius: "12px",
                  my: 2,
                }}
              >
                <Button
                  sx={{
                    ...btnSty,
                    color: type === "full time" ? "#fff" : "#1E1E1E",
                    background:
                      type === "full time" ? theme.palette.primary.main : "",
                  }}
                  onClick={() => setType("full time")}
                >
                  Full time
                </Button>
                <Button
                  sx={{
                    ...btnSty,
                    color: type === "temporary" ? "#fff" : "#1E1E1E",
                    background:
                      type === "temporary" ? theme.palette.primary.main : "",
                  }}
                  onClick={() => setType("temporary")}
                >
                  Temporary
                </Button>
              </Stack>
            </Box>
            <JobApplication
              type={type}
              jobDetails={job_details}
              changeJobDetails={setJob_Details}
              changeSelectDate={handleDateChange}
              document={documents}
              handleDocumentChange={handleDocument}
              selectDate={selectedDate}
              setTypedDepartment={setTyped_Department}
              typed_department={typed_department}
              setContractDuration={setContract_duration}
              benefits={benefits}
              handleBenefits={handleBenefits}
              nurses={nurses}
              handleNurses={handleNurses}
            />{" "}
          </Box>
        )}
        {!preview && (
          <PreviewJob
            date={selectedDate}
            documents={documents}
            data={job_details}
            type={type}
            benefits={benefits}
            handlePreview={handlePreview}
            contractDuration={contract_duration}
            isOutsourced={isOutsourced}
            outsourcingDetails={outsourcingDetails}
          />
        )}
      </Box>
    </Stack>
  );
};

export default PostJobContent;
